import { AnalysisStatus } from '@/enums';
import InputsMapper from '@/form/components/InputsMapper';
import { OmitControlControlledInputProps } from '@/form/components/RHFControlledInput';
import useRHFForm from '@/form/hooks/useRHFForm';
import { AnalysisEntity } from '@/interfaces';
import {
  useGetAdminSettingQuery,
  useUpdateCommercialAnalysisBatch,
  useUpdateResearchAnalysisBatch,
} from '@/queries';
import { generateOptions } from '@/utils';
import { CustomButton, CustomModal } from '@amili-v3/amili-v3-design';
import { notification } from 'antd';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { isEmpty, pick } from 'lodash';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import z from 'zod';
import { AnalysisTabPane } from '../home/AnalysisTabs';
import { updateAnalysisSchema } from '../schemas/analysis.schema';

type UpdateAnalysisBatchModalProps = {
  analysisRecord: AnalysisEntity;
  isOpen: boolean;
  toggleModal: () => void;
};

const UpdateAnalysisBatchModal = (props: UpdateAnalysisBatchModalProps) => {
  const { analysisRecord, isOpen, toggleModal } = props;
  const isToBeAnalysisBatch =
    analysisRecord?.status === AnalysisStatus.TO_BE_ANALYSED;
  const { pathname } = useLocation();
  const currentActiveTab = pathname.split('/').at(-1) ?? '';
  const isResearchAnalysisTab = currentActiveTab === AnalysisTabPane.RESEARCH;

  const { data: adminSettingData } = useGetAdminSettingQuery();
  const allowBuckets = adminSettingData?.analysisBatchAllowedBuckets ?? [];

  const { Form, methods } = useRHFForm({
    schema: updateAnalysisSchema,
  });

  const projectOptions = [
    { label: analysisRecord?.project?.name, value: analysisRecord?.projectId },
  ];

  useEffect(() => {
    if (!isEmpty(analysisRecord) && isOpen) {
      methods.reset({
        forceRun: analysisRecord.forceRun,
        projectId: analysisRecord.projectId,
        allowPartialRun: analysisRecord.allowPartialRun,
        dateOfCreation: dayjs(analysisRecord.dateOfCreation),
        analysisBucketPath: analysisRecord?.analysisBucketPath,
        analysisBatchSyncBucket: analysisRecord?.analysisBatchSyncBucket,
      });
    } else {
      methods.reset({});
    }
  }, [methods, analysisRecord, isOpen]);

  const updateCommercialAnalysisMutation = useUpdateCommercialAnalysisBatch({
    onSuccess: () => {
      notification.success({ message: t('common.success') });
      toggleModal();
    },
  });

  const updateResearchAnalysisMutation = useUpdateResearchAnalysisBatch({
    onSuccess: () => {
      notification.success({ message: t('common.success') });
      toggleModal();
    },
  });

  const handleSubmit = (values: z.infer<typeof updateAnalysisSchema>) => {
    if (isResearchAnalysisTab) {
      updateResearchAnalysisMutation.mutateAsync({
        id: analysisRecord.id,
        data: pick(values, ['analysisBatchSyncBucket', 'analysisBucketPath']),
      });
    } else {
      updateCommercialAnalysisMutation.mutateAsync({
        id: analysisRecord.id,
        data: pick(values, ['analysisBatchSyncBucket', 'analysisBucketPath']),
      });
    }
  };

  const currentBucket = methods.watch('analysisBatchSyncBucket');
  const fields: OmitControlControlledInputProps<
    z.infer<typeof updateAnalysisSchema>
  >[] = [
    {
      disabled: true,
      name: 'dateOfCreation',
      inputType: 'datePicker',
      label: t('extraction.dateOfCreation'),
    },
    {
      disabled: true,
      name: 'projectId',
      inputType: 'select',
      options: projectOptions,
      label: t('titles.project'),
    },
    {
      disabled: !isToBeAnalysisBatch,
      name: 'analysisBatchSyncBucket',
      inputType: 'select',
      options: generateOptions(allowBuckets),
      label: t('analysis.analysisBucket'),
      allowClear: true,
      showSearch: true,
    },
    {
      disabled: !isToBeAnalysisBatch,
      name: 'analysisBucketPath',
      label: t('project.analysisBucketPath'),
      allowClear: true,
      hidden: !currentBucket,
      required: !!currentBucket,
    },
    // {
    //   inputType: 'switch',
    //   name: 'allowPartialRun',
    //   label: (
    //     <Tooltip
    //       placement="top"
    //       className="allow-partial-run-label"
    //       title={t('analysis.allowPartialRunTooltipForAnalysis')}
    //     >
    //       <span>{t('batch.allowPartialRun')}</span>

    //       <Icon name="exclamationMark" />
    //     </Tooltip>
    //   ),
    // },
    // {
    //   name: 'forceRun',
    //   inputType: 'switch',
    //   label: (
    //     <Tooltip
    //       placement="top"
    //       className="allow-partial-run-label"
    //       title={t('batch.forceRunTooltip')}
    //     >
    //       <span>{t('batch.forceRun')}</span>

    //       <Icon name="exclamationMark" />
    //     </Tooltip>
    //   ),
    // },
  ];

  return (
    <CustomModal
      footer={null}
      onCancel={toggleModal}
      open={isOpen}
      title={t('analysis.updateAnalysisBatch')}
      destroyOnClose
    >
      <Form
        onSubmit={handleSubmit}
        onError={console.info}
        className="analysis-form"
      >
        <InputsMapper fields={fields} />

        <CustomButton
          size="large"
          width="100%"
          htmlType="submit"
          className="submit-btn"
          loading={
            updateCommercialAnalysisMutation.isPending ||
            updateResearchAnalysisMutation.isPending
          }
        >
          {t('analysis.updateAnalysisBatch')}
        </CustomButton>
      </Form>
    </CustomModal>
  );
};

export default UpdateAnalysisBatchModal;
